import api from "../index";

// ==================== PROMPTS CRUD ====================

// Get all prompts paginated (super admin)
export const getAllPromptsPaginated = async (
  page: number = 1,
  perPage: number = 20,
  filters: {
    status?: string;
    visibility?: string;
    category?: string;
    featured?: string;
    search?: string;
  } = {}
) => {
  const params = new URLSearchParams({
    page: page.toString(),
    perPage: perPage.toString(),
  });

  if (filters.status) params.append("status", filters.status);
  if (filters.visibility) params.append("visibility", filters.visibility);
  if (filters.category) params.append("category", filters.category);
  if (filters.featured) params.append("featured", filters.featured);
  if (filters.search) params.append("search", filters.search);

  return await api.get(`/super/prompts?${params.toString()}`);
};

// Get prompt by ID
export const getPromptById = async (id: string) => {
  return await api.get(`/super/prompts/${id}`);
};

// Update prompt by ID
export const updatePromptById = async (id: string, data: any) => {
  return await api.put(`/super/prompts/${id}`, data);
};

// Delete prompt by ID
export const deletePromptById = async (id: string) => {
  return await api.delete(`/super/prompts/${id}`);
};

// ==================== MODERATION ====================

// Get pending review prompts
export const getPendingReviewPrompts = async (
  page: number = 1,
  perPage: number = 20
) => {
  return await api.get(
    `/super/prompts/moderation/pending?page=${page}&perPage=${perPage}`
  );
};

// Get flagged prompts
export const getFlaggedPrompts = async (
  page: number = 1,
  perPage: number = 20
) => {
  return await api.get(
    `/super/prompts/moderation/flagged?page=${page}&perPage=${perPage}`
  );
};

// Approve prompt
export const approvePrompt = async (id: string) => {
  return await api.post(`/super/prompts/${id}/approve`);
};

// Reject prompt
export const rejectPrompt = async (id: string, reason: string) => {
  return await api.post(`/super/prompts/${id}/reject`, { reason });
};

// Bulk approve prompts
export const bulkApprovePrompts = async (promptIds: string[]) => {
  return await api.post("/super/prompts/moderation/bulk-approve", { promptIds });
};

// Bulk reject prompts
export const bulkRejectPrompts = async (promptIds: string[], reason: string) => {
  return await api.post("/super/prompts/moderation/bulk-reject", {
    promptIds,
    reason,
  });
};

// Clear flags
export const clearPromptFlags = async (id: string) => {
  return await api.post(`/super/prompts/${id}/clear-flags`);
};

// ==================== FEATURING ====================

// Get featured prompts
export const getFeaturedPrompts = async (limit: number = 20) => {
  return await api.get(`/super/prompts/featured/list?limit=${limit}`);
};

// Feature prompt
export const featurePrompt = async (
  id: string,
  options: {
    editorNote?: string;
    staffPick?: boolean;
    featuredOrder?: number;
  } = {}
) => {
  return await api.post(`/super/prompts/${id}/feature`, options);
};

// Unfeature prompt
export const unfeaturePrompt = async (id: string) => {
  return await api.post(`/super/prompts/${id}/unfeature`);
};

// ==================== ANALYTICS ====================

// Get prompts analytics overview
export const getPromptsAnalyticsOverview = async () => {
  return await api.get("/super/prompts/analytics/overview");
};

// ==================== CATEGORIES ====================

// Get all categories
export const getAllPromptCategories = async (includeInactive: boolean = false) => {
  return await api.get(
    `/super/prompts/categories?includeInactive=${includeInactive}`
  );
};

// Get category tree
export const getPromptCategoryTree = async () => {
  return await api.get("/super/prompts/categories/tree");
};

// Create category
export const createPromptCategory = async (data: {
  name: string;
  slug?: string;
  description?: string;
  icon?: string;
  color?: string;
  parent?: string;
  order?: number;
  isFeatured?: boolean;
}) => {
  return await api.post("/super/prompts/categories", data);
};

// Update category
export const updatePromptCategory = async (id: string, data: any) => {
  return await api.put(`/super/prompts/categories/${id}`, data);
};

// Delete category
export const deletePromptCategory = async (id: string, hard: boolean = false) => {
  return await api.delete(`/super/prompts/categories/${id}?hard=${hard}`);
};

// Reorder categories
export const reorderPromptCategories = async (
  categories: { id: string; parent?: string }[]
) => {
  return await api.put("/super/prompts/categories/reorder", { categories });
};

// Seed default categories
export const seedDefaultCategories = async () => {
  return await api.post("/super/prompts/categories/seed");
};
